import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';

import { useNavigate } from 'react-router-dom';

function formatPhone(val) {
  const d = val.replace(/[^0-9]/g, '').replace(/^1/, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return '+1 (' + d;
  if (d.length <= 6) return '+1 (' + d.slice(0,3) + ') ' + d.slice(3);
  return '+1 (' + d.slice(0,3) + ') ' + d.slice(3,6) + '-' + d.slice(6);
}

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState({ name: "", agency: "", phone: "", license_number: "", twilio_phone: "", twilio_sid: "", twilio_token: "", sendgrid_key: "", apify_key: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwStatus, setPwStatus] = useState(null); // { type: 'success'|'error', msg: '' }
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    api.me().then(u => setForm(f => ({ ...f, name: u.name || "", agency: u.agency || "", phone: u.phone || "", license_number: u.license_number || "", twilio_phone: u.twilio_phone || "", twilio_sid: u.twilio_sid || "", twilio_token: u.twilio_token || "", sendgrid_key: u.sendgrid_key || "", apify_key: u.apify_key || "" }))).catch(() => {});
  }, []);

  const handleSave = async () => {
    try { await api.saveSettings(form); toast({ message: 'Settings saved successfully.' }); }
    catch (err) { toast({ message: err.message, type: 'error' }); }
  };

  const handleChangePassword = async () => {
    setPwStatus(null);
    if (!pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirmPassword) {
      return setPwStatus({ type: 'error', msg: 'All three password fields are required.' });
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      return setPwStatus({ type: 'error', msg: 'New passwords do not match.' });
    }
    if (pwForm.newPassword.length < 8) {
      return setPwStatus({ type: 'error', msg: 'New password must be at least 8 characters.' });
    }
    setPwLoading(true);
    try {
      await api.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPwStatus({ type: 'success', msg: 'Password changed successfully.' });
    } catch (err) {
      setPwStatus({ type: 'error', msg: err.message });
    } finally {
      setPwLoading(false);
    }
  };

  const sections = [
    { title: "Account", icon: "ti-user", fields: [
      { k: "name", label: "Full name" },
      { k: "agency", label: "Agency name" },
      { k: "phone", label: "Phone number", fmt: "phone" },
      { k: "license_number", label: "License number" },
    ]},
    { title: "Twilio SMS", icon: "ti-message", fields: [
      { k: "twilio_phone", label: "Twilio phone number", fmt: "phone" },
      { k: "twilio_sid", label: "Account SID" },
      { k: "twilio_token", label: "Auth token", type: "password" },
    ]},
    { title: "AI & Integrations", icon: "ti-sparkles", fields: [
      { k: "sendgrid_key", label: "SendGrid API key", type: "password" },
      { k: "apify_key", label: "Apify API token", type: "password" },
    ]},
  ];

  return (
    <div style={{ maxWidth: 620, display: "flex", flexDirection: "column", gap: 14 }}>
      {sections.map(s => (
        <div key={s.title} style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 20px rgba(83,74,183,0.05)" }}>
          <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 8px #534AB7" }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{s.title}</span>
          </div>
          <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            {s.fields.map(f => (
              <div key={f.k} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text2)", minWidth: 160, flexShrink: 0 }}>{f.label}</span>
                <input value={form[f.k] || ""} onChange={e => set(f.k, f.fmt === 'phone' ? formatPhone(e.target.value) : e.target.value)} type={f.type || "text"} style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 12, color: "var(--text)" }} />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* AI Mode info */}
      <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 20px rgba(83,74,183,0.05)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 8px #534AB7" }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>AI Mode</span>
        </div>
        <div style={{ padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 10, background: "rgba(83,74,183,0.06)", border: "0.5px solid rgba(83,74,183,0.2)" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", boxShadow: "0 0 6px #1D9E75" }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Selective mode</div>
              <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>Toggle AI on or off per lead in the Leads tab. You stay in full control of who the AI contacts.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription */}
      <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 20px rgba(83,74,183,0.05)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 8px #534AB7" }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Subscription</span>
        </div>
        <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>Current plan</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#534AB7" }}>{user?.plan || "Basic"}</div>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 20px rgba(83,74,183,0.05)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 8px #534AB7" }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Change Password</span>
        </div>
        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            { k: 'currentPassword', label: 'Current password' },
            { k: 'newPassword',     label: 'New password' },
            { k: 'confirmPassword', label: 'Confirm new password' },
          ].map(f => (
            <div key={f.k} style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text2)", minWidth: 160, flexShrink: 0 }}>{f.label}</span>
              <input
                type="password"
                value={pwForm[f.k]}
                onChange={e => setPwForm(p => ({ ...p, [f.k]: e.target.value }))}
                style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 12, color: "var(--text)" }}
              />
            </div>
          ))}
          {pwStatus && (
            <div style={{ padding: "8px 12px", borderRadius: 7, fontSize: 12, fontWeight: 500,
              background: pwStatus.type === 'success' ? "rgba(29,158,117,0.1)" : "rgba(226,75,74,0.1)",
              border: `0.5px solid ${pwStatus.type === 'success' ? "rgba(29,158,117,0.3)" : "rgba(226,75,74,0.2)"}`,
              color: pwStatus.type === 'success' ? "#0F6E56" : "#F09595"
            }}>
              {pwStatus.type === 'success' ? '✓ ' : ''}{pwStatus.msg}
            </div>
          )}
          <div>
            <button onClick={handleChangePassword} disabled={pwLoading} style={{ padding: "9px 22px", borderRadius: 8, border: "none", background: "#534AB7", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: pwLoading ? 0.7 : 1 }}>
              {pwLoading ? "Updating..." : "Change password"}
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={handleSave} style={{ padding: "10px 28px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#534AB7,#1D9E75)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 4px 15px rgba(83,74,183,0.3)" }}>Save settings</button>
        <button onClick={async () => { try { const r = await api.testEmail(); alert(r.message); } catch (e) { alert('Error: ' + e.message); } }} style={{ padding: "10px 24px", borderRadius: 8, border: "0.5px solid rgba(29,158,117,0.4)", background: "transparent", color: "#1D9E75", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>Send test email</button>
        {user?.email === 'agentjack1244@gmail.com' && (
          <button onClick={() => navigate('/dashboard/admin')} style={{ padding: "10px 24px", borderRadius: 8, border: "0.5px solid rgba(83,74,183,0.4)", background: "rgba(83,74,183,0.06)", color: "#534AB7", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <i className="ti ti-shield" style={{ marginRight: 6 }} aria-hidden="true" />Admin Panel
          </button>
        )}
      </div>
    </div>
  );
}
